<template>
    <div class='bubbleviz'>
        <svg class='bubbleviz__svg'
            :width='svgWidth'
            :height='svgHeight'
            :viewBox='`0 0 ${svgWidth} ${svgHeight}`'
            :style='`--text-scale: ${1 / svgScale}`'
            ref='svg'
            preserveAspectRatio='xMidYMid meet'>

            <defs>
                <filter id='bubbleviz__filter--inset-shadow' x='-50%' y='-50%' width='200%' height='200%'>
                    <feComponentTransfer in='SourceAlpha'>
                        <feFuncA type='table' tableValues='1 0' />
                    </feComponentTransfer>
                    <feGaussianBlur stdDeviation='15'/>
                    <feOffset dx='0' dy='2' result='offsetblur'/>
                    <feFlood flood-color='#467ED1' flood-opacity='0.75' result='color'/>
                    <feComposite in2='offsetblur' operator='in'/>
                    <feComposite in2='SourceAlpha' operator='in' />
                    <feMerge>
                        <feMergeNode in='SourceGraphic' />
                        <feMergeNode />
                    </feMerge>
                </filter>
            </defs>

            <template v-if='bubbles'>
                <g v-for='bubble in bubbles.children'
                    class='bubble'
                    :key='bubble.data.id'
                    :style='{
                        "--x": bubble.x,
                        "--y": bubble.y,
                        "--r": bubble.r,
                        "--inner-r": innerBubbleRadius(bubble)
                    }'
                    :class='{
                        "bubble--is-small": bubble.data.isSmall
                    }'>

                    <g class='bubble__outer'>
                        <svg class='bubble__outer__svg' :width='bubble.r * 2' :height='bubble.r * 2'>
                            <defs>
                                <clipPath :id='`bubble__clip--${bubble.data.id}`'>
                                    <circle class='bubble__circles__clip' />
                                </clipPath>
                                <radialGradient :id='`bubble__rg--${bubble.data.id}`'>
                                    <stop offset='0' stop-opacity='1' stop-color='white'/>
                                    <stop offset='0.0859' stop-opacity='0.99447' stop-color='white'/>
                                    <stop offset='0.1748' stop-opacity='0.97821' stop-color='white'/>
                                    <stop offset='0.2653' stop-opacity='0.95168' stop-color='white'/>
                                    <stop offset='0.3561' stop-opacity='0.91533' stop-color='white'/>
                                    <stop offset='0.4459' stop-opacity='0.86963' stop-color='white'/>
                                    <stop offset='0.5334' stop-opacity='0.81504' stop-color='white'/>
                                    <stop offset='0.6173' stop-opacity='0.75202' stop-color='white'/>
                                    <stop offset='0.6963' stop-opacity='0.68104' stop-color='white'/>
                                    <stop offset='0.7690' stop-opacity='0.60256' stop-color='white'/>
                                    <stop offset='0.8341' stop-opacity='0.51703' stop-color='white'/>
                                    <stop offset='0.8903' stop-opacity='0.42493' stop-color='white'/>
                                    <stop offset='0.9363' stop-opacity='0.32672' stop-color='white'/>
                                    <stop offset='0.9708' stop-opacity='0.22285' stop-color='white'/>
                                    <stop offset='0.9925' stop-opacity='0.11379' stop-color='white'/>
                                    <stop offset='1' stop-opacity='0' stop-color='white'/>
                                </radialGradient>
                                <mask :id='`bubble__mask--${bubble.data.id}`'>
                                    <circle class='bubble__circles__clip' :fill='`url(#bubble__rg--${bubble.data.id})`' style='transform: translate(-25%, -25%) scale(130%, 130%)' />
                                </mask>
                            </defs>
                            <g
                                class='bubble__circles bubble__circles--outer'
                                :clip-path='`url(#bubble__clip--${bubble.data.id})`'
                                :mask='`url(#bubble__mask--${bubble.data.id})`'>
                                <circle class='bubble__circles__bg' />
                                <circle class='bubble__circles__highlight-bottom' />
                                <circle class='bubble__circles__highlight-mid' />
                                <circle class='bubble__circles__highlight-top' />
                            </g>
                        </svg>
                    </g>

                    <line 
                        v-if='bubble.data.nearterm !== 0'
                        class='bubble__line'
                        :x1='bubble.x'
                        :y1='bubble.y - bubble.r + 30'
                        :x2='bubble.x'
                        :y2='bubble.y' 
                        stroke-width='1' 
                        vector-effect='non-scaling-stroke' />

                    <g v-if='bubble.data.nearterm !== 0' class='bubble__inner'>
                        <svg class='bubble__inner__svg' :width='innerBubbleRadius(bubble) * 2' :height='innerBubbleRadius(bubble) * 2'>
                            <defs>
                                <clipPath :id='`bubble__clip--${bubble.data.id}-inner`'>
                                    <circle class='bubble__circles__clip' />
                                </clipPath>
                            </defs>
                            <g class='bubble__circles bubble__circles--inner' :clip-path='`url(#bubble__clip--${bubble.data.id}-inner)`'>
                                <circle class='bubble__circles__bg' />
                                <circle class='bubble__circles__highlight-bottom' />
                                <circle class='bubble__circles__highlight-mid' />
                                <circle class='bubble__circles__highlight-top' />
                            </g>
                        </svg>
                    </g>

                    <g 
                        class='bubble__text' 
                        :style='{
                            "--title-scale": scale(bubble.data.longterm, this.aoiLongtermMin, this.aoiLongtermMax, 1, 2),
                            "--value-scale": scale(bubble.data.longterm, this.aoiLongtermMin, this.aoiLongtermMax, 1, 1.6)
                        }'>

                        <a :xlink:href="`#${bubble.data.slug}`" >
                            <g class='bubble__title'>
                                <template v-for='(word, i) in bubble.data.splitTitle' :key='i'>
                                    <text
                                        class='bubble__title__text'
                                        :style='`--i: ${i}`'
                                        :dy='`${(-1 * bubble.data.splitTitle.length / 2 + 0.5) + (1.1 * i)}em`'
                                        alignment-baseline='middle'
                                        text-anchor='middle'>
                                        {{ word }}
                                    </text>
                                </template>
                            </g>
                        </a>

                        <g v-if='bubble.data.nearterm !== 0'
                            class='bubble__value bubble__value--nearterm'
                            alignment-baseline='middle'
                            text-anchor='middle'>

                            <text v-if='globals' class='bubble__value__year'>
                                by {{ globals.nearterm_end_year }}
                            </text>
                            <text class='bubble__value__num'>
                                <tspan class='bubble__value__num__text'>
                                    {{ bubble.data.nearterm }}<tspan class='bubble__value__million'>M</tspan>
                                </tspan>
                            </text>
                            <text class='bubble__value__unit'>
                                MTCO<tspan class='bubble__value__unit__sub'>2</tspan>e
                            </text>
                        </g>

                        <g
                            class='bubble__value bubble__value--longterm'
                            alignment-baseline='middle'
                            text-anchor='middle'>

                            <text v-if='globals' class='bubble__value__year'>
                                by {{ globals.longterm_end_year }}
                            </text>
                            <text class='bubble__value__num'>
                                <tspan class='bubble__value__num__text'>
                                    {{ bubble.data.longterm }}<tspan class='bubble__value__million'>M</tspan>
                                </tspan>
                            </text>
                            <text class='bubble__value__unit'>
                                MTCO<tspan class='bubble__value__sub'>2</tspan>e
                            </text>
                        </g>
                    </g>
                </g>
            </template>

        </svg>

        <div class='bubbleviz__caption page-content'>
            <div class='bubbleviz__caption__text'>
                {{ caption }}
            </div>
        </div>
    </div>
</template>


<script>
import { defineComponent } from 'vue';
import { hierarchy, pack } from 'd3-hierarchy';

export default defineComponent({
    name: 'BubbleViz',
    props: ['id'],
    data: () => ({
        aois: null,
        globals: null,
        caption: null,

        // the max times bigger the largest bubble is than the smallest
        bubbleMaxScale: 15,

        // if the bubble is smaller than this times the largest,
        // it's designated as "small" bubble
        bubbleIsSmallScale: 9,

        svgWidth: 500,
        svgHeight: 500,
        svgScale: 1
    }),
    computed: {
        // min and max aoi goal values
        aoiNeartermMin () {
            return this.aois ? Math.min(...this.aois.map(aoi => aoi.nearterm)) : null;
        },
        aoiNeartermMax () {
            return this.aois ? Math.max(...this.aois.map(aoi => aoi.nearterm)) : null;
        },
        aoiLongtermMin () {
            return this.aois ? Math.min(...this.aois.map(aoi => aoi.longterm)) : null;
        },
        aoiLongtermMax () {
            return this.aois ? Math.max(...this.aois.map(aoi => aoi.longterm)) : null;
        },

        bubbles() {
            if (!this.aois) return;

            // add size data
            const aoiTransformedData = {
                name: 'top',
                children: this.aois.map(aoi => ({
                    ...aoi,
                    size: this.scale(aoi.longterm, this.aoiLongtermMin, this.aoiLongtermMax, 1, this.bubbleMaxScale),
                    // size: aoi.longterm,
                    isSmall: aoi.longterm < this.aoiLongtermMax / this.bubbleIsSmallScale,
                    parent: 'top'
                }))
            };

            // Generate a D3 hierarchy
            const rootHierarchy = hierarchy(aoiTransformedData)
                .sum(d => d.size)
                .sort((a, b) => {
                    return b.value - a.value
                });

            // Pack the circles inside the viewbox
            return pack()
                .size([this.svgWidth, this.svgHeight])
                .padding(0)(rootHierarchy);
        }
    },
    mounted () {
        this.fetchData();
        this.measureSvgScale();
        window.addEventListener('resize', this.resize);
    },
    beforeUnmount () {
        window.removeEventListener('resize', this.resize);
    },
    methods: {
        fetchData() {
            const aoiUrl = '/api/collections/areas_of_investigation/entries/?limit=7&fields=id,title,aoi_nearterm_impact_total_adjusted,aoi_longterm_impact_total_adjusted,slug';

            const globalsUrl = '/api/globals/dataviz';

            const pageURL = `/api/collections/pages/entries/${this.id}/?fields=data_visualization_caption`;

            fetch(aoiUrl).then(async res => {
                const data = await res.json();
                this.aois = data.data.map(item => {
                    return {
                        ...item,
                        nearterm: item.aoi_nearterm_impact_total_adjusted,
                        longterm: item.aoi_longterm_impact_total_adjusted,
                        splitTitle: item.title.replace("& ", "&\xA0").split(" ")
                    };
                });
            }).catch(err => console.error('error', err));

            fetch(globalsUrl).then(async res => {
                const data = await res.json();
                this.globals = data.data;
            }).catch(err => console.error('error', err));

            fetch(pageURL).then(async res => {
                const data = await res.json();
                this.caption = data.data.data_visualization_caption;
            }).catch(err => console.error('error', err));
        },
        measureSvgScale () {
            const box = this.$refs.svg.getBoundingClientRect();
            const scaleX = box.width / this.svgWidth;
            const scaleY = box.height / this.svgHeight;
            this.svgScale = scaleX > scaleY ? scaleY : scaleX;
        },
        innerBubbleRadius (bubble) {
            const rawRadius = bubble.r * bubble.data.nearterm / bubble.data.longterm;
            const minRadius = bubble.r / 3;
            const maxRadius = 1;
            return this.scale(rawRadius, this.aoiNeartermMin, this.aoiNeartermMax, minRadius, maxRadius);
        },
        scale(value, inMin, inMax, outMin, outMax) {
            const result = (value - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;

            if (result < outMin) return outMin;
            else if (result > outMax) return outMax;

            return result;
        },
        resize () {
            this.measureSvgScale();
        }
    }
})
</script>
